/* eslint-disable react/no-unescaped-entities */
'use client'

import { Check, ChevronsUpDown, Plus } from 'lucide-react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import * as React from 'react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { ImageSquareCropDialog } from '@/components/ui/image-square-crop-dialog'
import { Input } from '@/components/ui/input'
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar'
import {
  fetchTeamsShared,
  fetchWorkspacePlans,
  formatPlanLabel,
  getWorkspacePlanCode,
  personalWorkspaceName,
  type TeamApiItem,
  upsertTeamInTeamsCache,
} from '@/components/workspace/workspace-switcher-shared'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { toast } from '@/hooks/use-toast'
import { fetchWithAuth } from '@/utils/api-error-handler'
import { planBadgeClass } from '@/utils/plan-badge'
import { userInitials } from '@/utils/user-initials'
import { appendWorkspaceScopeToPath, normalizeTeamId, PERSONAL_WORKSPACE_ID } from '@/utils/workspace-scope'

type Team = {
  id: string
  name: string
  logoUrl?: string
  planCode?: string | null
  planLabel?: string | null
  kind: 'org' | 'personal'
}

type TeamCreateResponse = {
  id: string
  name: string
  logo_url?: string | null
  permission_scope?: 'read' | 'write'
}

type TeamLogoUploadResponse = {
  team_id: string
  logo_url: string
  timestamp: string
}

const MAX_LOGO_SIZE_BYTES = 10 * 1024 * 1024
const ALLOWED_LOGO_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp'])

export function TeamSwitcher({
  user,
  enableRemoteFetch = true,
}: {
  user: { name: string; email: string; avatar?: string }
  enableRemoteFetch?: boolean
}) {
  const { state } = useSidebar()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [open, setOpen] = React.useState(false)
  const [teamOptions, setTeamOptions] = React.useState<Team[]>([])
  const [isLoadingTeams, setIsLoadingTeams] = React.useState(false)
  const [hasLoadedTeams, setHasLoadedTeams] = React.useState(false)
  const [isCreateTeamOpen, setIsCreateTeamOpen] = React.useState(false)
  const [isCreatingTeam, setIsCreatingTeam] = React.useState(false)
  const [newTeamName, setNewTeamName] = React.useState('')
  const [newTeamError, setNewTeamError] = React.useState<string | null>(null)
  const [newTeamLogoFile, setNewTeamLogoFile] = React.useState<File | null>(null)
  const [newTeamLogoPreviewUrl, setNewTeamLogoPreviewUrl] = React.useState<string | null>(null)
  const [teamLogoCropOpen, setTeamLogoCropOpen] = React.useState(false)
  const [teamLogoCropSrc, setTeamLogoCropSrc] = React.useState<string | null>(null)
  const isLoadingTeamsRef = React.useRef(false)
  const { activeTeamId, selectedWorkspaceId, setActiveTeamId } = useWorkspace()

  const personalTeam = React.useMemo<Team>(() => {
    const planCode = getWorkspacePlanCode(PERSONAL_WORKSPACE_ID)
    const planLabel = enableRemoteFetch ? formatPlanLabel(planCode) : 'Guest'
    return {
      id: PERSONAL_WORKSPACE_ID,
      name: enableRemoteFetch ? personalWorkspaceName(user?.name, user?.email) : 'Fastfold',
      planCode,
      planLabel,
      kind: 'personal'
    }
  }, [enableRemoteFetch, user?.email, user?.name])

  const loadTeams = React.useCallback(async (forceRefresh: boolean = false) => {
    if (!enableRemoteFetch) {
      setTeamOptions([personalTeam])
      setHasLoadedTeams(true)
      setIsLoadingTeams(false)
      return
    }
    if ((hasLoadedTeams && !forceRefresh) || isLoadingTeamsRef.current) return
    isLoadingTeamsRef.current = true
    setIsLoadingTeams(true)
    const API_URL = process.env.NEXT_PUBLIC_API_URL
    const url = API_URL ? `${API_URL}/v1/teams` : '/v1/teams'
    try {
      const teamItems = await fetchTeamsShared(url)
      await fetchWorkspacePlans(API_URL || null, forceRefresh)
      const personalPlanCode = getWorkspacePlanCode(PERSONAL_WORKSPACE_ID)
      const teamsFromApi = teamItems.map((item) => {
        const planCode = getWorkspacePlanCode(item.id)
        const planLabel = formatPlanLabel(planCode)
        return {
          id: item.id,
          name: item.name,
          logoUrl: item.logo_url || undefined,
          planCode,
          planLabel,
          kind: 'org' as const
        }
      })
      setTeamOptions([
        {
          ...personalTeam,
          planCode: personalPlanCode,
          planLabel: formatPlanLabel(personalPlanCode) ?? personalTeam.planLabel ?? null
        },
        ...teamsFromApi
      ])
    } catch {
      setTeamOptions([personalTeam])
    } finally {
      setHasLoadedTeams(true)
      setIsLoadingTeams(false)
      isLoadingTeamsRef.current = false
    }
  }, [enableRemoteFetch, hasLoadedTeams, personalTeam])

  React.useEffect(() => {
    if (!enableRemoteFetch) return
    if (open) {
      void loadTeams(true)
    }
  }, [enableRemoteFetch, open, loadTeams])

  React.useEffect(() => {
    if (!enableRemoteFetch) return
    if (!hasLoadedTeams && !isLoadingTeams) {
      void loadTeams(true)
    }
  }, [enableRemoteFetch, hasLoadedTeams, isLoadingTeams, loadTeams])

  React.useEffect(() => {
    if (!enableRemoteFetch) return
    // If a team is already selected from URL/local scope, preload once so
    // switcher can render the real team name/logo before opening dropdown.
    if (!activeTeamId || hasLoadedTeams || isLoadingTeams) return
    void loadTeams(true)
  }, [activeTeamId, enableRemoteFetch, hasLoadedTeams, isLoadingTeams, loadTeams])

  const teams = React.useMemo(() => {
    const base = teamOptions.length > 0 ? teamOptions : [personalTeam]
    const personalFromBase = base.find((item) => item.id === PERSONAL_WORKSPACE_ID)
    const withoutPersonal = base.filter((item) => item.id !== PERSONAL_WORKSPACE_ID)
    const personalResolved: Team = {
      ...personalFromBase,
      ...personalTeam,
      planCode: personalFromBase?.planCode ?? personalTeam.planCode,
      planLabel: personalFromBase?.planLabel ?? personalTeam.planLabel,
    }
    return [personalResolved, ...withoutPersonal]
  }, [personalTeam, teamOptions])

  React.useEffect(() => {
    if (!hasLoadedTeams) return
    const urlTeamId = normalizeTeamId(searchParams.get('team_id'))
    if (urlTeamId) return
    const exists = teams.some((item) => item.id === selectedWorkspaceId)
    if (!exists && activeTeamId) {
      setActiveTeamId(null)
    }
  }, [activeTeamId, hasLoadedTeams, searchParams, selectedWorkspaceId, setActiveTeamId, teams])

  const selected = React.useMemo(() => {
    const existing = teams.find((t) => t.id === selectedWorkspaceId)
    if (existing) return existing
    if (selectedWorkspaceId !== PERSONAL_WORKSPACE_ID) {
      return {
        id: selectedWorkspaceId,
        name: 'Team workspace',
        kind: 'org' as const
      }
    }
    return teams[0]
  }, [selectedWorkspaceId, teams])
  const selectedHasPlan = Boolean(selected?.planLabel ?? selected?.planCode)
  const showSelectedPlanLoader = !selectedHasPlan && (!hasLoadedTeams || isLoadingTeams)
  const showSelectedPlanUnavailable = !selectedHasPlan && hasLoadedTeams && !isLoadingTeams

  const handleSelectWorkspace = (workspaceId: string) => {
    const nextTeamId = workspaceId === PERSONAL_WORKSPACE_ID ? null : workspaceId
    setActiveTeamId(nextTeamId)

    const currentPath = pathname || '/'
    const currentQuery = searchParams.toString()
    const currentUrl = currentQuery ? `${currentPath}?${currentQuery}` : currentPath
    const scopedUrl = appendWorkspaceScopeToPath(currentUrl, nextTeamId)
    if (scopedUrl !== currentUrl) {
      router.push(scopedUrl)
      return
    }
    // If URL scope is unchanged, force page-level refetch.
    router.refresh()
  }

  const handleCreateTeam = async () => {
    const teamName = newTeamName.trim()
    if (!teamName) {
      setNewTeamError('Team name is required')
      return
    }

    const API_URL = process.env.NEXT_PUBLIC_API_URL
    const url = API_URL ? `${API_URL}/v1/teams` : '/v1/teams'
    setIsCreatingTeam(true)
    setNewTeamError(null)

    try {
      const response = await fetchWithAuth(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ name: teamName })
      })
      if (!response.ok) {
        throw new Error(`Failed to create team (${response.status})`)
      }

      const created = (await response.json()) as TeamCreateResponse
      const createdScope = created.permission_scope === 'read' ? 'read' : 'write'
      let uploadedLogoUrl = created.logo_url || null

      if (newTeamLogoFile) {
        try {
          const logoUrl = API_URL ? `${API_URL}/v1/teams/${created.id}/logo` : `/v1/teams/${created.id}/logo`
          const formData = new FormData()
          formData.append('team_logo', newTeamLogoFile)
          const logoResponse = await fetchWithAuth(logoUrl, {
            method: 'POST',
            body: formData
          })
          if (!logoResponse.ok) throw new Error(`Failed to upload team logo (${logoResponse.status})`)
          const logoPayload = (await logoResponse.json()) as TeamLogoUploadResponse
          uploadedLogoUrl = logoPayload.logo_url
        } catch (error) {
          toast({ title: 'Team created, but logo upload failed', variant: 'destructive' })
          console.error(error)
        }
      }

      const createdItem: TeamApiItem = {
        id: created.id,
        name: created.name,
        logo_url: uploadedLogoUrl,
        permission_scope: createdScope
      }

      upsertTeamInTeamsCache(createdItem)

      await fetchWorkspacePlans(API_URL || null, true)
      const createdPlanCode = getWorkspacePlanCode(created.id)
      const createdTeam: Team = {
        id: created.id,
        name: created.name,
        logoUrl: uploadedLogoUrl || undefined,
        planCode: createdPlanCode,
        planLabel: formatPlanLabel(createdPlanCode),
        kind: 'org'
      }
      setTeamOptions((prev) => {
        const base = prev.length > 0 ? prev : [personalTeam]
        const deduped = base.filter((item) => item.id !== createdTeam.id)
        return [deduped[0] ?? personalTeam, createdTeam, ...deduped.slice(1)]
      })
      setHasLoadedTeams(true)

      setActiveTeamId(created.id)
      setOpen(false)
      setIsCreateTeamOpen(false)
      setNewTeamName('')
      setNewTeamLogoFile(null)
      if (newTeamLogoPreviewUrl) {
        URL.revokeObjectURL(newTeamLogoPreviewUrl)
        setNewTeamLogoPreviewUrl(null)
      }
      toast({ title: 'Team created' })
      router.push(appendWorkspaceScopeToPath(`/teams/${created.id}/settings`, created.id))
    } catch (error) {
      setNewTeamError('Failed to create team')
      toast({ title: 'Failed to create team', variant: 'destructive' })
      console.error(error)
    } finally {
      setIsCreatingTeam(false)
    }
  }

  const onCreateTeamLogoSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!ALLOWED_LOGO_TYPES.has(file.type)) {
      setNewTeamError('Invalid logo type. Use PNG, JPG, or WEBP')
      event.target.value = ''
      return
    }

    if (file.size > MAX_LOGO_SIZE_BYTES) {
      setNewTeamError('Logo is too large. Maximum size is 10MB')
      event.target.value = ''
      return
    }

    if (teamLogoCropSrc) URL.revokeObjectURL(teamLogoCropSrc)
    setTeamLogoCropSrc(URL.createObjectURL(file))
    setTeamLogoCropOpen(true)
    if (newTeamError) setNewTeamError(null)
    event.target.value = ''
  }

  const clearCreateTeamLogo = () => {
    setNewTeamLogoFile(null)
    if (newTeamLogoPreviewUrl) {
      URL.revokeObjectURL(newTeamLogoPreviewUrl)
      setNewTeamLogoPreviewUrl(null)
    }
    if (teamLogoCropSrc) {
      URL.revokeObjectURL(teamLogoCropSrc)
      setTeamLogoCropSrc(null)
    }
    setTeamLogoCropOpen(false)
  }

  const handleTeamLogoCropped = (file: File) => {
    if (teamLogoCropSrc) URL.revokeObjectURL(teamLogoCropSrc)
    setTeamLogoCropSrc(null)
    setNewTeamLogoFile(file)
    if (newTeamLogoPreviewUrl) URL.revokeObjectURL(newTeamLogoPreviewUrl)
    setNewTeamLogoPreviewUrl(URL.createObjectURL(file))
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <div className="relative">
            <SidebarMenuButton
              size="lg"
              className={`!h-10 w-full pr-10 ${state === 'collapsed' ? 'justify-center' : ''}`}
              aria-label="Go to home"
              title={selected?.name || 'Go to home'}
              onClick={() => router.push(appendWorkspaceScopeToPath('/', activeTeamId))}
            >
              <div className={`flex items-center ${state === 'collapsed' ? 'justify-center w-full' : ''}`}>
                <Avatar className="h-5 w-5 rounded-full">
                  {(selected?.kind === 'org' ? selected.logoUrl : user?.avatar) ? (
                    <AvatarImage
                      src={(selected?.kind === 'org' ? selected.logoUrl : user?.avatar) || ''}
                      alt={selected?.name || user.name}
                      className="rounded-full"
                    />
                  ) : null}
                  <AvatarFallback className="rounded-full text-[11px] font-medium">
                    {selected?.kind === 'personal'
                      ? userInitials(user.name, user.email)
                      : (selected?.name || 'Team').slice(0, 1).toUpperCase()}
                  </AvatarFallback>
                </Avatar>

                {state === 'expanded' && (
                  <div className="flex items-center flex-1 pl-2 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="truncate text-sm font-normal">{selected?.name}</span>
                      {(selected?.planLabel ?? selected?.planCode) ? (
                        <span
                          className={[
                            'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none',
                            planBadgeClass(selected?.planCode ?? selected?.planLabel ?? '')
                          ].join(' ')}
                        >
                          {selected?.planLabel ?? formatPlanLabel(selected?.planCode) ?? selected?.planCode}
                        </span>
                      ) : showSelectedPlanLoader ? (
                        <span
                          aria-label="Loading plan"
                          className="shrink-0 rounded-full bg-muted animate-pulse h-4 w-16"
                        />
                      ) : showSelectedPlanUnavailable ? (
                        <span className="shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none bg-muted text-muted-foreground">
                          Plan unavailable
                        </span>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            </SidebarMenuButton>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
                aria-label="Open team selector"
              >
                <ChevronsUpDown className="h-4 w-4 shrink-0" />
              </button>
            </DropdownMenuTrigger>
          </div>

          <DropdownMenuContent
            side="bottom"
            align="start"
            sideOffset={10}
            className="w-[320px] rounded-xl border bg-background p-1.5 shadow-xl"
          >
            <div className="space-y-1 px-1 py-1">
              {isLoadingTeams ? (
                <div className="px-2 py-2 text-xs text-muted-foreground">Loading teams...</div>
              ) : null}
              {teams.map((t) => {
                const isSelected = t.id === selectedWorkspaceId
                const rowHasPlan = Boolean(t.planLabel ?? t.planCode)
                const showRowPlanLoader = !rowHasPlan && (!hasLoadedTeams || isLoadingTeams)
                const showRowPlanUnavailable = !rowHasPlan && hasLoadedTeams && !isLoadingTeams
                return (
                  <DropdownMenuItem
                    key={t.id}
                    className="cursor-pointer rounded-lg px-2.5 py-2"
                    onClick={() => handleSelectWorkspace(t.id)}
                  >
                    <div className="flex w-full items-center gap-3 min-w-0">
                      <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                        <Avatar className="h-6 w-6 rounded-full">
                          {(t.kind === 'org' ? t.logoUrl : user?.avatar) ? (
                            <AvatarImage
                              src={(t.kind === 'org' ? t.logoUrl : user?.avatar) || ''}
                              alt={t.name}
                              className="rounded-full"
                            />
                          ) : null}
                          <AvatarFallback className="rounded-full text-[10px] font-medium">
                            {t.kind === 'personal'
                              ? userInitials(user.name, user.email)
                              : t.name.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="truncate text-sm font-medium">{t.name}</span>
                          {rowHasPlan ? (
                            <span
                              className={[
                                'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none',
                                planBadgeClass(t.planCode ?? t.planLabel ?? '')
                              ].join(' ')}
                            >
                              {t.planLabel ?? formatPlanLabel(t.planCode) ?? t.planCode}
                            </span>
                          ) : showRowPlanLoader ? (
                            <span
                              aria-label="Loading plan"
                              className="shrink-0 rounded-full bg-muted animate-pulse h-4 w-16"
                            />
                          ) : showRowPlanUnavailable ? (
                            <span className="shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none bg-muted text-muted-foreground">
                              Plan unavailable
                            </span>
                          ) : null}
                        </div>
                      </div>

                      {isSelected ? <Check className="h-5 w-5 text-foreground" /> : <span className="h-5 w-5" />}
                    </div>
                  </DropdownMenuItem>
                )
              })}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="cursor-pointer rounded-lg px-2.5 py-2"
                onClick={() => {
                  setOpen(false)
                  setNewTeamError(null)
                  setIsCreateTeamOpen(true)
                }}
              >
                <div className="flex w-full items-center gap-3 min-w-0">
                  <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <Plus className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">Create Team</div>
                    <div className="truncate text-xs text-muted-foreground">Collaborate in a shared workspace</div>
                  </div>
                </div>
              </DropdownMenuItem>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
        <Dialog
          open={isCreateTeamOpen}
          onOpenChange={(next) => {
            setIsCreateTeamOpen(next)
            if (!next) {
              setNewTeamError(null)
              setNewTeamName('')
              clearCreateTeamLogo()
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create a team</DialogTitle>
              <DialogDescription>Enter a team name to create your shared workspace.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Input
                placeholder="Team name"
                value={newTeamName}
                onChange={(event) => {
                  setNewTeamName(event.target.value)
                  if (newTeamError) setNewTeamError(null)
                }}
              />
              <div className="rounded-md border p-3">
                <div className="mb-2 text-xs font-medium text-muted-foreground">Team logo (optional)</div>
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10 rounded-full">
                    {newTeamLogoPreviewUrl ? <AvatarImage src={newTeamLogoPreviewUrl} alt="Team logo preview" /> : null}
                    <AvatarFallback>{(newTeamName || 'Team').charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <label className="inline-flex cursor-pointer items-center rounded-md border px-3 py-1.5 text-xs font-medium">
                    Choose image
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={onCreateTeamLogoSelected}
                    />
                  </label>
                  {newTeamLogoFile ? (
                    <button type="button" className="text-xs text-muted-foreground underline" onClick={clearCreateTeamLogo}>
                      Remove
                    </button>
                  ) : null}
                </div>
              </div>
              {newTeamError ? <p className="text-xs text-destructive">{newTeamError}</p> : null}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateTeamOpen(false)}>
                Cancel
              </Button>
              <Button disabled={isCreatingTeam} onClick={() => void handleCreateTeam()}>
                {isCreatingTeam ? 'Creating' : 'Continue'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ImageSquareCropDialog
          open={teamLogoCropOpen}
          onOpenChange={(open) => {
            if (!open && teamLogoCropSrc) {
              URL.revokeObjectURL(teamLogoCropSrc)
              setTeamLogoCropSrc(null)
            }
            setTeamLogoCropOpen(open)
          }}
          imageSrc={teamLogoCropSrc}
          onCancel={() => {
            if (teamLogoCropSrc) URL.revokeObjectURL(teamLogoCropSrc)
            setTeamLogoCropSrc(null)
          }}
          onCropComplete={handleTeamLogoCropped}
          title="Crop team logo"
          description="Position and zoom so your logo fits the square without stretching."
          outputFileName="team-logo.jpg"
        />
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

